import {
  areasOf, clamp, defaultMaskSettings, defaultShadingSettings, isConvexQuad, newTemplateId, parseTemplate, slugify,
  titleCaseId, withAreaIdentity,
  AREA_PRESETS, DEFAULT_PRINT_AREA_IN, MAX_PRINT_AREAS, PHOTO_MAX_SIDE, PHOTO_MIN_SIDE,
  type MockupKind, type MockupTemplate, type MockupView, type Point, type PrepareOptions, type PrintArea,
  type Quad, type Rgb,
} from '@eip/engine';
import type { MockupPrepared } from '../worker/protocol';

/** A photo's size, in pixels — the file's own, or the one the worker prepared. */
export interface PhotoSize { width: number; height: number }

/**
 * Everything the template editor is holding: the fields a `MockupTemplate`
 * carries, the `PrepareOptions` every control edits, and the last preparation
 * the worker sent back. Deliberately pixel-free — the preview and mask-outline
 * bitmaps belong to the component, which has to close them; only their
 * measurements live here.
 */
export interface EditorState {
  /** The transient id the worker holds the in-progress photo and maps under
   *  (`mockupPrepareEditor`/`mockupSaveEditor`/`mockupUnload`). Never a template id. */
  editorId: string;
  /** The id a save writes to: a user template being re-edited keeps its own (and
   *  a duplicate is seeded with a fresh `u-` id by the caller); a brand-new
   *  template has none until `editorToTemplate` mints one. */
  templateId?: string;
  name: string;
  /** As typed. Stored slugified — `editorToTemplate` is what decides. */
  style: string;
  view: MockupView;
  kind: MockupKind;
  colorName: string;
  /** The garment's own colour: measured by the first preparation, then the
   *  user's if they pick one off the photo. */
  garmentColor: Rgb;
  /** The photo's size AS THE WORKER WILL PREPARE IT — `photoFromBytes` caps the
   *  long side at `PHOTO_MAX_SIDE`, so this can be smaller than the file. Every
   *  quad below is in this space, which is also the space the template is saved
   *  in. Null until a photo is chosen (or when the browser could not measure one). */
  photo: PhotoSize | null;
  /** What the template records as `photo.src`. A user template's photo is read
   *  from the Blob the store holds, so this is a label rather than a path. */
  photoSrc: string;
  /**
   * Every printable area on this blank, PRIMARY FIRST — the one the mask flood is
   * seeded from, the one a job prints into without being asked, and the one
   * `parseTemplate` names after the view. The rest are the sleeves and pockets a
   * shop adds by hand; each carries an id unique within the template (the handle a
   * job's extra prints address it by) and the name the picker shows.
   *
   * Empty until the first preparation derives one — or, for a seeded editor, from
   * the moment the seed's own areas are read. `opts.printAreas` mirrors this list:
   * it is what every preparation is asked for, and `withAreas` is the only thing
   * that writes either, so the two cannot drift.
   */
  areas: PrintArea[];
  /** The area the corner handles edit and the size fields address; `areas[0].id` unless
   *  the shop picked another, and '' while there are no areas. Never saved: it is a
   *  choice about the editor, not about the blank. */
  activeAreaId: string;
  opts: PrepareOptions;
  /** The last preparation the worker returned, minus its bitmaps. Null until the
   *  first one lands — which is also what "this editor has nothing to save" means. */
  prepared: MockupPrepared | null;
  /** Edited since the last save: the modal says so rather than trapping Cancel
   *  behind a confirm. */
  dirty: boolean;
  /** This editor started from an existing template AND still has that
   *  template's photo, so `garmentColor` above is a colour somebody chose
   *  (often with the eyedropper, off a blank the mask measures wrong). The
   *  first preparation must not overwrite it — re-editing a user template used
   *  to silently re-measure and discard that choice. Cleared when the user
   *  picks a different photo: a new garment is a new colour to measure. */
  seeded: boolean;
}

/** A mask covering less than a tenth, or more than nine tenths, of the photo
 *  went wrong — the same band `MockupPrepared.coverage` is documented against. */
export const COVERAGE_MIN = 0.1, COVERAGE_MAX = 0.9;

/** The shortest side any template photo may have. `PHOTO_MIN_SIDE` gates the
 *  LONG side in the worker; a 6000 × 80 strip passes that and is still useless
 *  as a blank, so the editor — the only place a photo enters the app by hand —
 *  holds the short side to this as well. */
export const PHOTO_MIN_SHORT_SIDE = 300;

export const NOT_SEPARABLE_HINT = 'Background could not be separated: this template will not be recolourable';

/** The size `photoFromBytes` will hand `prepareTemplate`, computed the same way
 *  (long side capped at `PHOTO_MAX_SIDE`, both sides rounded), so the editor can
 *  put a seeded quad into the right space BEFORE the first prepare tells it. */
export function fitPhotoSize(width: number, height: number): PhotoSize {
  const long = Math.max(width, height);
  if (long <= PHOTO_MAX_SIDE) return { width, height };
  const s = PHOTO_MAX_SIDE / long;
  return { width: Math.max(1, Math.round(width * s)), height: Math.max(1, Math.round(height * s)) };
}

/** Why this photo cannot be a template, or null. The message matches the
 *  worker's, so a file that slips past this (one the browser could not measure)
 *  is refused in the same words. */
export function photoTooSmall(size: PhotoSize): string | null {
  const short = Math.min(size.width, size.height), long = Math.max(size.width, size.height);
  return short < PHOTO_MIN_SHORT_SIDE || long < PHOTO_MIN_SIDE ? 'photo too small for a template' : null;
}

/** Told once, after the first prepare: the quad being edited (and saved) is in
 *  the DOWNSCALED photo's pixels, not the file's. */
export function downscaleNotice(natural: PhotoSize | null, prepared: MockupPrepared | null): string | null {
  if (!natural || !prepared) return null;
  if (natural.width === prepared.width && natural.height === prepared.height) return null;
  return `Photo was downscaled to ${PHOTO_MAX_SIDE} px`;
}

/** Move a print area from one photo size into another, clamped to the target.
 *  Scaling both axes by their own factor keeps the area over the same part of
 *  the garment and cannot break convexity (an affine map of a convex quad). */
export function scalePrintArea(pa: PrintArea, from: PhotoSize, to: PhotoSize): PrintArea {
  const sx = to.width / from.width, sy = to.height / from.height;
  const p = ([x, y]: Point): Point => [clamp(x * sx, 0, to.width), clamp(y * sy, 0, to.height)];
  return { ...pa, quad: { tl: p(pa.quad.tl), tr: p(pa.quad.tr), br: p(pa.quad.br), bl: p(pa.quad.bl) } };
}

// ---- the areas ---------------------------------------------------------------

/** The band a print area's physical size is held to — `parseTemplate`'s own, so a
 *  size the editor accepts is one a save will keep. The inch fields clamp to it too. */
export const AREA_IN_MIN = 1, AREA_IN_MAX = 40;

/** What "Add area" offers. `custom` is the one with no preset behind it: a square
 *  big enough to grab and meant to be resized, for a leg hit or a hood print. */
export type AreaPreset = 'sleeve' | 'pocket' | 'custom';
export const ADD_AREA_PRESETS: readonly AreaPreset[] = ['sleeve', 'pocket', 'custom'];
const CUSTOM_AREA = { id: 'area', widthIn: 6, heightIn: 6 };
const PRESET_AREA: Record<AreaPreset, { id: string; widthIn: number; heightIn: number }> = {
  sleeve: { id: 'sleeve', ...AREA_PRESETS.sleeve },
  pocket: { id: 'pocket', ...AREA_PRESETS.pocket },
  custom: CUSTOM_AREA,
};
/** The shop-facing name for an id nobody has named: the preset's own, else the id
 *  title-cased — exactly how the engine names an area, so `sleeve-2` reads "Sleeve 2"
 *  here and in the picker. */
const nameForId = (id: string): string =>
  (AREA_PRESETS as Record<string, { name: string } | undefined>)[id]?.name ?? titleCaseId(id);

/**
 * Write a new list of areas, keeping `opts.printAreas` — what every preparation is
 * asked for — in step with it, and the active area pointing at one that exists.
 *
 * `opts.widthIn`/`heightIn` follow the PRIMARY area's size. They are only read when
 * no areas are sent at all, which is the moment after a new photo is chosen: the
 * area the worker then derives comes back at the size the last primary had, rather
 * than snapping back to 12 × 16.
 */
function withAreas(state: EditorState, areas: PrintArea[], activeAreaId = state.activeAreaId): EditorState {
  const active = areas.some((a) => a.id === activeAreaId) ? activeAreaId : areas[0]?.id ?? '';
  return {
    ...state, areas, activeAreaId: active,
    opts: {
      ...state.opts,
      printAreas: areas.length ? areas : undefined,
      widthIn: areas[0]?.widthIn ?? state.opts.widthIn,
      heightIn: areas[0]?.heightIn ?? state.opts.heightIn,
    },
  };
}

/**
 * The primary area of a BACK blank is "Back", not "Chest".
 *
 * `prepareTemplate` derives every auto area as `chest` (it never sees the view), and
 * a pre-3C template's single area was named from the view at parse time — so without
 * this, a back blank authored here would offer a print area called "Chest". Applied
 * both when the view changes and when the first preparation's areas are adopted (the
 * view may have been set before the photo, or the photo replaced afterwards).
 *
 * Only the exact untouched preset pair is swapped: an area the shop has renamed, or
 * one whose wanted id is already taken by another area, keeps what it has.
 */
function withViewIdentity(areas: PrintArea[], view: MockupView): PrintArea[] {
  const first = areas[0];
  const want = view === 'back' ? 'back' : 'chest', other = want === 'back' ? 'chest' : 'back';
  if (!first || first.id !== other || first.name !== AREA_PRESETS[other].name) return areas;
  if (areas.some((a, i) => i > 0 && a.id === want)) return areas;
  return [{ ...first, id: want, name: AREA_PRESETS[want].name }, ...areas.slice(1)];
}

/** The area the handles are on, or null before the first preparation. */
export function activeArea(state: EditorState): PrintArea | null {
  return state.areas.find((a) => a.id === state.activeAreaId) ?? null;
}

/** How many photo pixels an inch is worth on this blank, read off the primary area:
 *  the mean of its quad's two horizontal edges (so a keystoned area still answers
 *  sensibly) over the inches that width stands for. */
export function pxPerInch(primary: PrintArea): number {
  const q = primary.quad;
  const top = Math.hypot(q.tr[0] - q.tl[0], q.tr[1] - q.tl[1]);
  const bottom = Math.hypot(q.br[0] - q.bl[0], q.br[1] - q.bl[1]);
  const w = (top + bottom) / 2;
  return primary.widthIn > 0 && w > 0 ? w / primary.widthIn : 0;
}

/** `sleeve`, then `sleeve-2`, `sleeve-3`, … — the first slug not already spoken for.
 *  Ids are what a job's extra prints name an area by, so a clash is not cosmetic. */
function uniqueAreaId(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  for (let n = 2; ; n++) if (!taken.has(`${base}-${n}`)) return `${base}-${n}`;
}

/**
 * Add a preset-sized area, centred on the photo and made ACTIVE so the handles are
 * already on it.
 *
 * Its size comes from the primary area's scale (`pxPerInch`) rather than from the
 * photo's: the chest quad is the only thing on a blank that says what an inch is
 * worth in these pixels. A preset that would not fit is clamped inside the photo —
 * an area sticking out of it is one `parseTemplate` refuses at save time.
 *
 * Refused (state handed straight back) before the first preparation, and at
 * `MAX_PRINT_AREAS`, which is the ceiling a template may carry.
 */
export function addArea(state: EditorState, preset: AreaPreset): EditorState {
  const photo = state.photo, primary = state.areas[0];
  if (!photo || !primary || state.areas.length >= MAX_PRINT_AREAS) return state;
  const spec = PRESET_AREA[preset];
  const pxIn = pxPerInch(primary) || photo.width / DEFAULT_PRINT_AREA_IN.widthIn;
  const w = clamp(Math.round(spec.widthIn * pxIn), 2, photo.width);
  const h = clamp(Math.round(spec.heightIn * pxIn), 2, photo.height);
  const left = clamp(Math.round(photo.width / 2 - w / 2), 0, photo.width - w);
  const top = clamp(Math.round(photo.height / 2 - h / 2), 0, photo.height - h);
  const right = left + w, bottom = top + h;
  const id = uniqueAreaId(spec.id, new Set(state.areas.map((a) => a.id ?? '')));
  const area: PrintArea = {
    id, name: nameForId(id),
    quad: { tl: [left, top], tr: [right, top], br: [right, bottom], bl: [left, bottom] },
    widthIn: spec.widthIn, heightIn: spec.heightIn,
  };
  return withAreas({ ...state, dirty: true }, [...state.areas, area], id);
}

/** Rename one area, exactly as typed — a half-typed name has to survive the keystroke
 *  that made it. The id — what a job's prints address — is untouched: renaming "Sleeve"
 *  to "Left sleeve" must not orphan the prints already pointing at it. */
export function renameArea(state: EditorState, id: string, name: string): EditorState {
  if (!state.areas.some((a) => a.id === id)) return state;
  return withAreas({ ...state, dirty: true }, state.areas.map((a) => (a.id === id ? { ...a, name } : a)));
}

/** Leaving the name field: an area left blank takes the name a save would give it
 *  anyway (`parseTemplate` fills a blank one from the id), and a padded one is
 *  trimmed — so the row never shows a name different from the one it would save. */
export function commitAreaName(state: EditorState, id: string): EditorState {
  const area = state.areas.find((a) => a.id === id);
  if (!area) return state;
  const name = (area.name ?? '').trim() || nameForId(id);
  if (name === area.name) return state;
  return withAreas({ ...state, dirty: true }, state.areas.map((a) => (a === area ? { ...a, name } : a)));
}

/** Drop a named area. The PRIMARY is refused: it seeds the mask flood and is where a
 *  job prints by default, so a blank without one has nothing to print on. */
export function removeArea(state: EditorState, id: string): EditorState {
  const at = state.areas.findIndex((a) => a.id === id);
  if (at <= 0) return state;
  return withAreas({ ...state, dirty: true }, state.areas.filter((_, i) => i !== at));
}

/** One area's physical size. Clamped to the 1–40 in band `parseTemplate` enforces, and
 *  a field cleared mid-edit (NaN) leaves that dimension alone rather than committing
 *  garbage. The quad does not move: inches are what the quad MEANS at the press. */
export function setAreaSize(state: EditorState, id: string, widthIn: number, heightIn: number): EditorState {
  if (!state.areas.some((a) => a.id === id)) return state;
  const size = (v: number, was: number) => (Number.isFinite(v) ? clamp(v, AREA_IN_MIN, AREA_IN_MAX) : was);
  return withAreas({ ...state, dirty: true }, state.areas.map((a) =>
    a.id === id ? { ...a, widthIn: size(widthIn, a.widthIn), heightIn: size(heightIn, a.heightIn) } : a));
}

/** Which area the handles and the size fields address. Not an edit: nothing about the
 *  saved template changes, so this leaves `dirty` alone. */
export function setActiveArea(state: EditorState, id: string): EditorState {
  if (id === state.activeAreaId || !state.areas.some((a) => a.id === id)) return state;
  return { ...state, activeAreaId: id };
}

/** Change the view — and, with it, the name of an UNTOUCHED primary area
 *  (`withViewIdentity`: a back blank's is "Back", not the "Chest" a preparation derives). */
export function setView(state: EditorState, view: MockupView): EditorState {
  return withAreas({ ...state, view, dirty: true }, withViewIdentity(state.areas, view));
}

/**
 * A fresh editor, or one seeded from a template being duplicated or re-edited.
 * A seed carries its own print areas — ALL of them — so the first prepare is told
 * where to flood the mask from and which quads to normalise the shading over,
 * instead of deriving a new area; a blank editor sends none at all and adopts
 * whatever `defaultPrintArea` returns.
 *
 * A pre-3C seed that only ever had `printArea` becomes one area, named after the
 * view (`withAreaIdentity`), which is the same identity `parseTemplate` gives it.
 *
 * The seed's id is kept as `templateId` — the caller is responsible for putting
 * a FRESH `newTemplateId('u')` on a seed taken from a built-in, or the save
 * would write over that built-in's id.
 */
export function newEditorState(seed?: MockupTemplate): EditorState {
  const kind: MockupKind = seed?.kind ?? 'flat';
  const areas = seed ? withAreaIdentity(areasOf(seed), seed.view === 'back' ? 'back' : 'chest') : [];
  return {
    editorId: newTemplateId('e'),
    templateId: seed?.id,
    name: seed?.name ?? '',
    style: seed?.style ?? '',
    view: seed?.view ?? 'front',
    kind,
    colorName: seed?.colorName ?? '',
    garmentColor: seed ? [...seed.garmentColor] : [128, 128, 128],
    photo: seed ? { width: seed.photo.width, height: seed.photo.height } : null,
    photoSrc: seed?.photo.src ?? '',
    areas,
    activeAreaId: areas[0]?.id ?? '',
    opts: {
      kind,
      mask: seed ? { ...seed.mask } : defaultMaskSettings(kind),
      shading: seed ? { ...seed.shading } : defaultShadingSettings(),
      printAreas: areas.length ? areas : undefined,
      // Only read when NO areas are sent — i.e. on a blank editor's first prepare,
      // where they are the inches `defaultPrintArea` derives its quad for.
      widthIn: areas[0]?.widthIn ?? DEFAULT_PRINT_AREA_IN.widthIn,
      heightIn: areas[0]?.heightIn ?? DEFAULT_PRINT_AREA_IN.heightIn,
    },
    prepared: null,
    dirty: false,
    seeded: !!seed,
  };
}

/**
 * Take on a chosen photo: record the size the worker will actually prepare and
 * drop the last preparation, so the next one counts as the first again.
 *
 * `keepPrintArea` is for the photo a seed arrived with (a duplicate): EVERY area
 * is rescaled into the prepared photo's space, which is what keeps a template
 * whose photo is larger than `PHOTO_MAX_SIDE` from coming back with a quad
 * outside the photo. A photo the user picked is a different garment — its areas
 * are derived from the new mask instead.
 */
export function withPhoto(state: EditorState, natural: PhotoSize | null, src: string, keepPrintArea = false): EditorState {
  const photo = natural ? fitPhotoSize(natural.width, natural.height) : null;
  const from = state.photo;
  const areas = !keepPrintArea ? []
    : from && photo ? state.areas.map((a) => scalePrintArea(a, from, photo))
    : state.areas;
  // `keepPrintArea` is the seed's own photo arriving; anything else is a
  // different garment, whose colour the next preparation should measure.
  return withAreas({ ...state, photo, photoSrc: src, prepared: null, dirty: true, seeded: keepPrintArea && state.seeded }, areas);
}

/**
 * Fold a preparation into the editor. The FIRST one is the only one allowed to
 * write the print areas and the garment colour: from then on the quads are the
 * user's (dragged by the handles, added by "Add area") and the colour may have
 * been picked off the photo, and the worker echoes both back on every prepare —
 * adopting them again would undo every edit the moment the next preparation
 * landed.
 *
 * On that first one the whole LIST is adopted, ids and names included: for a
 * blank editor it is the area `defaultPrintArea` derived, and for a seeded one
 * it is the seed's own areas coming back with the identity `prepareTemplate`
 * put on them.
 *
 * A SEEDED editor (a template being re-edited, or duplicated, with its own
 * photo) does not adopt the measured colour even on the first preparation: the
 * seed's `garmentColor` is already somebody's answer to this exact question,
 * and re-measuring it threw away an eyedropper pick every time the template was
 * opened again.
 */
export function applyPrepared(state: EditorState, prepared: MockupPrepared): EditorState {
  const first = state.prepared === null;
  const next: EditorState = {
    ...state,
    photo: { width: prepared.width, height: prepared.height },
    prepared,
    garmentColor: first && !state.seeded ? prepared.garmentColor : state.garmentColor,
  };
  if (!first) return next;
  // `withViewIdentity`: the worker derives a primary area as `chest` whatever the view,
  // so a back blank whose view was set before the photo must not adopt "Chest".
  const adopted = prepared.printAreas.length ? prepared.printAreas : [prepared.printArea];
  return withAreas(next, withViewIdentity(adopted, state.view));
}

/**
 * Drag one corner of a print area, in photo pixels. The corner is held inside
 * the photo, and a move that would fold the quad over itself (or flatten it) is
 * refused outright — returning the quad unchanged, so the handle simply stops
 * following the pointer rather than leaving an area `parseTemplate` would reject
 * at save time.
 */
export function moveQuadCorner(quad: Quad, corner: keyof Quad, to: Point, photo: PhotoSize): Quad {
  const moved: Point = [clamp(to[0], 0, photo.width), clamp(to[1], 0, photo.height)];
  const next: Quad = { ...quad, [corner]: moved };
  return isConvexQuad(next) ? next : quad;
}

/** `moveQuadCorner` on the area `areaId` names. A refused move — a fold, an area
 *  this editor does not have, a photo it has not measured — hands the SAME state
 *  back, which is what lets the drag skip the re-render for that frame. */
export function moveCorner(state: EditorState, areaId: string, corner: keyof Quad, to: Point): EditorState {
  const photo = state.photo;
  const area = state.areas.find((a) => a.id === areaId);
  if (!photo || !area) return state;
  const quad = moveQuadCorner(area.quad, corner, to, photo);
  if (quad === area.quad) return state;
  return withAreas({ ...state, dirty: true }, state.areas.map((a) => (a === area ? { ...a, quad } : a)));
}

/**
 * The template this editor is describing, validated through `parseTemplate` —
 * the same gate the shipped blanks and the stored ones go through, so a quad
 * that is not convex or not inside the photo is caught here rather than in the
 * worker on the next load.
 *
 * `recolorable` is the worker's separability decision, not a choice: a blank
 * whose garment could not be told from its backdrop by colour must never be
 * offered for recolouring.
 */
export function editorToTemplate(state: EditorState, id = state.templateId ?? newTemplateId('u')): MockupTemplate {
  const prepared = state.prepared;
  if (!prepared) throw new Error('choose a photo and let it prepare first');
  // The editor's own list, primary first; the preparation's only while nothing has
  // been adopted from it yet (a state built by hand). `withAreaIdentity` is the
  // belt to the braces: an area with no id of its own would be one no print could
  // ever name.
  const areas = withAreaIdentity(
    state.areas.length ? state.areas : prepared.printAreas.length ? prepared.printAreas : [prepared.printArea],
    state.view === 'back' ? 'back' : 'chest',
  );
  return parseTemplate({
    version: 1,
    id,
    name: state.name.trim(),
    style: slugify(state.style),
    view: state.view,
    kind: state.kind,
    colorName: state.colorName.trim(),
    // The PREPARED size, always: the quad is in it, and it is the photo the
    // worker will decode from the stored Blob on every later load.
    photo: { width: prepared.width, height: prepared.height, src: state.photoSrc || 'photo' },
    garmentColor: state.garmentColor,
    recolorable: prepared.separable,
    // `printAreas` alone: `parseTemplate` reads it when it is there and hands back
    // the `printArea` alias itself, and writing both is how the two drift apart.
    printAreas: areas.map((a) => ({ id: a.id, name: a.name, quad: a.quad, widthIn: a.widthIn, heightIn: a.heightIn })),
    mask: { ...defaultMaskSettings(state.kind), ...state.opts.mask },
    shading: { ...defaultShadingSettings(), ...state.opts.shading },
  });
}

/**
 * Why this template cannot be saved yet, or null. Only what the editor cannot
 * make up for the user: a prepared photo and the three names `parseTemplate`
 * insists on. A suspicious mask is NOT one of these — `coverageWarning` says so
 * beside the button, and a shop that knows its photo is right may save anyway.
 */
export function canSave(state: EditorState): string | null {
  if (!state.photo || !state.prepared) return 'Choose a photo';
  if (!state.name.trim()) return 'Name the template';
  if (!slugify(state.style)) return 'Name the style';
  if (!state.colorName.trim()) return 'Name the colour';
  return null;
}

/**
 * Drop the editor's entry in the worker — but never before the preparation that
 * may still be creating it has finished.
 *
 * `mockupPrepareEditor` decodes the photo BEFORE it records the entry, so a
 * cancel during that first preparation used to post `mockupUnload` while the
 * worker was still inside `decodePhoto`: the unload deleted nothing, the decode
 * then created the entry, and a photo and its five maps (tens of megabytes) were
 * stranded in the worker for the life of the tab. Waiting on the preparation —
 * whether it succeeds or fails — is what makes the unload land after it.
 */
export function unloadAfter(inflight: Promise<unknown> | null | undefined, unload: () => Promise<unknown>): Promise<void> {
  return Promise.resolve(inflight ?? undefined)
    .catch(() => { /* a failed preparation still has to be unloaded after */ })
    .then(() => unload())
    .then(() => { /* nothing to hand back */ }, () => { /* worker gone: nothing to free */ });
}

/** "The mask went wrong" as a sentence, or null while the coverage is plausible. */
export function coverageWarning(state: EditorState): string | null {
  const p = state.prepared;
  if (!p || (p.coverage >= COVERAGE_MIN && p.coverage <= COVERAGE_MAX)) return null;
  return `Check the mask (coverage ${Math.round(p.coverage * 100)} %)`;
}
